import { X, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { PresignedUploadState } from "../models/presignedUpload";
import { cn } from "../lib/utils";

interface UploadProgressOverlayProps {
  state: PresignedUploadState;
  onCancel: () => void;
}

export function UploadProgressOverlay({
  state,
  onCancel,
}: UploadProgressOverlayProps) {
  if (!state.isUploading && state.files.length === 0) {
    return null;
  }

  const completedCount = state.files.filter(
    (f) => f.status === "completed",
  ).length;
  const hasErrors = state.files.some((f) => f.status === "error");

  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 rounded-lg border border-stone-200 bg-white p-4 shadow-lg">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {state.isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin text-stone-600" />
          ) : hasErrors ? (
            <AlertCircle className="h-4 w-4 text-red-500" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          )}
          <span className="text-sm font-medium text-stone-900">
            {state.isUploading
              ? `Uploading ${completedCount}/${state.files.length} files...`
              : hasErrors
                ? "Upload completed with errors"
                : "Upload complete"}
          </span>
        </div>
        {state.isUploading && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <Progress value={state.overallProgress} className="mb-3 h-2" />

      <div className="max-h-40 space-y-2 overflow-y-auto">
        {state.files.map((file) => (
          <div
            key={file.filename}
            className="flex items-center justify-between gap-2 text-xs"
          >
            <span
              className={cn(
                "truncate",
                file.status === "error" && "text-red-600",
                file.status === "completed" && "text-stone-600",
              )}
              title={file.filename}
            >
              {file.filename}
            </span>
            <div className="flex items-center gap-2">
              {file.status === "uploading" && (
                <span className="text-stone-500">{file.progress}%</span>
              )}
              {file.status === "completed" && (
                <CheckCircle2 className="h-3 w-3 text-green-500" />
              )}
              {file.status === "error" && (
                <AlertCircle className="h-3 w-3 text-red-500" />
              )}
              {file.status === "pending" && (
                <span className="text-stone-400">Pending</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
